// Route scrolling: top on a new page, the anchor for #hash links, and the saved
// position when the user goes back/forward. The SPA renders asynchronously, so a
// restore waits (briefly) until the page is tall enough.

const STORAGE_KEY = 'asans_scroll_v1';

export function createScrollMemory(storage, max = 60) {
  let map = {};
  try { map = JSON.parse(storage?.getItem(STORAGE_KEY) || '{}') || {}; } catch { map = {}; }
  const order = Object.keys(map);
  return {
    save(key, y) {
      if (!key) return;
      if (!(key in map)) order.push(key);
      map[key] = Math.max(0, Math.round(y));
      while (order.length > max) delete map[order.shift()];
    },
    get(key) { return key in map ? map[key] : undefined; },
    flush() { try { storage?.setItem(STORAGE_KEY, JSON.stringify(map)); } catch {} },
  };
}

// React Router gives the first entry of every full page load the same key `default`; positions of
// different pages must not overwrite each other.
export function historyEntryKey(location) {
  return !location.key || location.key === 'default' ? `default:${location.pathname}${location.search}` : location.key;
}

// Must run before the first render: the browser's own restoration otherwise moves the page after ours.
export function enableManualScrollRestoration(history = typeof window !== 'undefined' ? window.history : null) {
  try { if (history && 'scrollRestoration' in history) history.scrollRestoration = 'manual'; } catch {}
}

export function routeScrollAction({prevPath, path, navType, hash, saved, initialNav}) {
  if (prevPath === null || prevPath === undefined) {
    if (initialNav === 'back_forward' && saved > 0) return {type: 'restore', y: saved};
    if (hash) return {type: 'hash', hash};
    return {type: 'top'};
  }
  if (prevPath === path) return {type: 'none'};
  if (navType === 'POP' && saved !== undefined && saved !== null) return {type: 'restore', y: saved};
  if (hash) return {type: 'hash', hash};
  return {type: 'top'};
}

export function headerOffset() {
  const header = document.querySelector('.site-header');
  return header ? Math.round(header.getBoundingClientRect().height) + 12 : 0;
}

// Instant native jump (overrides any CSS smooth scroll-behavior).
export function jumpTo(y) {
  window.scrollTo({top: y, left: 0, behavior: 'instant'});
}

export function initialNavigationType() {
  try { return performance.getEntriesByType('navigation')[0]?.type || 'navigate'; } catch { return 'navigate'; }
}

const INPUTS = ['wheel', 'touchstart', 'keydown', 'pointerdown'];
// Keep trying until the target exists / the document is tall enough; any user input cancels.
export function settle(apply, timeout = 4000, onDone) {
  let stopped = false, frame = 0;
  const started = performance.now();
  const stop = () => {
    if (stopped) return;
    stopped = true; cancelAnimationFrame(frame);
    onDone?.();
    INPUTS.forEach(t => window.removeEventListener(t, stop, true));
  };
  INPUTS.forEach(t => window.addEventListener(t, stop, {capture: true, passive: true}));
  const tick = () => {
    if (stopped) return;
    if (apply() || performance.now() - started > timeout) { stop(); return; }
    frame = requestAnimationFrame(tick);
  };
  tick();
  return stop;
}

